import { css } from "@linaria/core"

import { mobileBreakpoint } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"

export const Markdown = css`
  font-family: var(--default-font-family), sans-serif;

  & > :first-child {
    margin-top: 0;
  }

  & > :last-child {
    margin-bottom: 0;
  }
`

export const Block = css`
  display: block;
  overflow-x: auto;
`

export const Blockquote = css`
  margin-left: 6px;
  padding-left: 16px;

  border-left: 4px solid var(--text-color-20);

  &:only-child > p:only-child {
    margin: 0;
  }
`

export const Code = css`
  padding: 2px 4px;
  border-radius: var(--border-radius-4);
  background-color: var(--text-color-5);

  ${textStyles.monospaceFont};
  ${textStyles.breakWord};

  pre & {
    padding: 0;
    border-radius: 0;
    background-color: transparent;
  }
`

export const HeadingStyles = {
  Heading1: css`
    font-size: 28px;
    font-weight: 400;
    font-family: var(--heading-font-family), sans-serif;
    line-height: 38px;

    letter-spacing: -0.02px;
    text-wrap: pretty;

    margin: 0;

    h1& {
      margin: 28px 0 0;
    }

    @media ${mobileBreakpoint} {
      font-size: 22px;
      font-weight: normal;
      line-height: 30px;

      h1& {
        margin-top: 22px;
      }
    }
  `,

  Heading2: css`
    font-size: 22px;
    font-weight: 400;
    font-family: var(--heading-font-family), sans-serif;
    line-height: 30px;

    letter-spacing: -0.02px;
    text-wrap: pretty;

    margin: 0;

    h2& {
      margin: 22px 0 0;
    }

    @media ${mobileBreakpoint} {
      font-size: 18px;
      font-weight: normal;
      line-height: 24px;

      h2& {
        margin-top: 18px;
      }
    }
  `,

  Heading3: css`
    font-size: 18px;
    font-weight: 400;
    font-family: var(--heading-font-family), sans-serif;
    line-height: 24px;

    letter-spacing: -0.01px;
    text-wrap: pretty;

    margin: 0;

    h3& {
      margin: 18px 0 0;
    }

    @media ${mobileBreakpoint} {
      font-size: 16px;
      font-weight: normal;
      line-height: 22px;

      h3& {
        margin-top: 16px;
      }
    }
  `,

  Heading4: css`
    font-size: 17px;
    font-weight: 400;
    font-family: var(--heading-font-family), sans-serif;
    line-height: 24px;

    letter-spacing: -0.01px;
    text-wrap: pretty;

    margin: 0;

    h4& {
      margin: 17px 0 0;
    }

    @media ${mobileBreakpoint} {
      font-size: 15px;
      font-weight: normal;
      line-height: 22px;

      h4& {
        margin-top: 15px;
      }
    }
  `,

  Heading5: css`
    font-size: 16px;
    font-weight: 500;
    font-family: var(--heading-font-family), sans-serif;
    line-height: 24px;

    letter-spacing: -0.01px;
    text-wrap: pretty;

    margin: 0;

    h5& {
      margin: 16px 0 0;
    }

    @media ${mobileBreakpoint} {
      font-size: 15px;
      font-weight: normal;
      line-height: 22px;

      h5& {
        margin-top: 15px;
      }
    }
  `,

  Heading6: css`
    ${textStyles.baseFontSize};

    font-weight: 500;
    font-family: var(--heading-font-family), sans-serif;

    letter-spacing: -0.01px;
    text-wrap: pretty;

    margin: 0;

    h6& {
      margin: 15px 0 0;
    }

    @media ${mobileBreakpoint} {
      font-size: 14px;
      font-weight: normal;
      line-height: 20px;

      h6& {
        margin-top: 14px;
      }
    }
  `
}

export const Image = css`
  max-width: 100%;
`

export const Li = css`
  list-style: inherit;
  text-align: left;

  &.task-list-item {
    margin-bottom: 4px;
  }

  .contains-task-list &.task-list-item {
    margin-top: 4px;
  }

  &.task-list-item input[type="checkbox"] {
    margin-right: 4px;
  }
`

export const Link = css`
  ${textStyles.breakWord};
`

export const Ol = css`
  list-style: decimal inside;
  padding-left: 0;

  p {
    &:first-child {
      display: inline;
    }

    &:only-child {
      &::after {
        display: block;
        height: 8px;
      }
    }
  }
`

export const P = css`
  margin: 8px 0;
  white-space: pre-wrap;
  text-wrap: pretty;
`

export const Pre = css`
  display: block;
  margin: 8px 0;
  padding: 8px;

  background-color: var(--island-background-color);
  border-radius: var(--border-radius-12);

  white-space: pre-wrap;
`

export const Strong = css`
  font-weight: 600;
`

export const Table = css`
  width: 100%;
  border-collapse: collapse;
`

export const Td = css`
  padding: 8px;
`

export const Th = css`
  ${textStyles.smallestFontSize};

  font-weight: normal;

  color: var(--secondary-text-color);
  text-align: left;
`

export const Tr = css`
  border-bottom: 1px solid var(--separator-color);
`

export const Ul = css`
  padding-left: 22px;
  list-style-type: disc;

  &.contains-task-list {
    list-style: none;
  }

  .${Markdown} > &.contains-task-list {
    padding-left: 0;
  }
`
